import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
} from 'recharts'
import { TabProps } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import { Star, TrendingUp, TrendingDown, Award, Target, CheckCircle } from 'lucide-react'
import api from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'

// Rating thresholds
const RATING_THRESHOLDS = {
  excellent: 90,
  good: 75,
  average: 60,
  poor: 0,
}

// Get rating status color and label
const getRatingStatus = (score: number) => {
  if (score >= RATING_THRESHOLDS.excellent) return { color: '#10b981', label: 'Excellent', bg: 'bg-green-100' }
  if (score >= RATING_THRESHOLDS.good) return { color: '#3b82f6', label: 'Good', bg: 'bg-blue-100' }
  if (score >= RATING_THRESHOLDS.average) return { color: '#f59e0b', label: 'Average', bg: 'bg-amber-100' }
  return { color: '#ef4444', label: 'Needs Improvement', bg: 'bg-red-100' }
}

interface PerformanceMetric {
  name: string
  score: number
  target: number
  trend: 'up' | 'down' | 'stable'
  available: boolean  // CR-4211: Whether this metric has real data
}

interface HistoricalRating {
  month: string
  quality: number
  delivery: number
  overall: number
}

export function PerformanceRatingsTab({ data, currency: _currency, filters: _filters }: TabProps) {
  const { formatNumber: _formatNumber } = useFormatters()
  const { user } = useAuth()
  const [performanceData, setPerformanceData] = useState<PerformanceMetric[]>([])
  const [historicalData, setHistoricalData] = useState<HistoricalRating[]>([])
  const [radarData, setRadarData] = useState<Array<{ subject: string; A: number; fullMark: number }>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPerformanceData = async () => {
      try {
        setLoading(true)

        // Fetch GRN data for performance calculation
        const grnsRes = await api.get('/purchasing/grns/', { params: { page_size: 500 } })
        const grns = grnsRes.data.results || grnsRes.data || []

        // Calculate performance metrics from GRN data
        let onTimeLines = 0
        let qualityPassedLines = 0

        grns.forEach((grn: Record<string, unknown>) => {
          if (grn['inspection_status'] === 'passed' || grn['inspection_status'] === 'received') qualityPassedLines++
          if (grn['actual_delivery_date'] && grn['expected_delivery_date']) {
            const actual = new Date(grn['actual_delivery_date'] as string)
            const expected = new Date(grn['expected_delivery_date'] as string)
            if (actual <= expected) onTimeLines++
          }
        })

        const hasGRNData = grns.length > 0
        const deliveryScore = hasGRNData ? (onTimeLines / grns.length) * 100 : 0
        const qualityScore = hasGRNData ? (qualityPassedLines / grns.length) * 100 : 0

        // CR-4211: Read targets from API data with fallbacks
        const deliveryTarget = data.targets?.otd ?? data.delivery?.otdTarget ?? 90
        const qualityTarget = data.targets?.ppm ? 95 : 95  // Quality target is separate from PPM
        const overallTarget = data.targets?.otif ?? data.quality?.otifTarget ?? 85

        // CR-4211: Only average metrics that have real data
        const availableScores = [
          ...(hasGRNData ? [deliveryScore, qualityScore] : []),
        ]
        const overallScore = availableScores.length > 0
          ? availableScores.reduce((a, b) => a + b, 0) / availableScores.length
          : 0

        const metrics: PerformanceMetric[] = [
          { name: 'Overall Rating', score: overallScore, target: overallTarget, trend: 'stable', available: hasGRNData },
          { name: 'On-Time Delivery', score: deliveryScore, target: deliveryTarget, trend: 'stable', available: hasGRNData },
          { name: 'Quality Score', score: qualityScore, target: qualityTarget, trend: 'stable', available: hasGRNData },
          { name: 'Responsiveness', score: 0, target: 85, trend: 'stable', available: false },
          { name: 'Compliance', score: 0, target: 90, trend: 'stable', available: false },
        ]
        setPerformanceData(metrics)

        // CR-4211: Generate historical trend by grouping GRNs by month
        if (hasGRNData) {
          const monthlyBuckets: Record<string, { total: number; onTime: number; qualityPassed: number }> = {}

          const _months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          grns.forEach((grn: Record<string, unknown>) => {
            const dateStr = (grn['receipt_date'] || grn['actual_delivery_date']) as string | undefined
            if (!dateStr) return
            const d = new Date(dateStr)
            const monthKey = `${_months[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`

            if (!monthlyBuckets[monthKey]) {
              monthlyBuckets[monthKey] = { total: 0, onTime: 0, qualityPassed: 0 }
            }
            monthlyBuckets[monthKey].total++

            if (grn['inspection_status'] === 'passed' || grn['inspection_status'] === 'received') monthlyBuckets[monthKey].qualityPassed++
            if (grn['actual_delivery_date'] && grn['expected_delivery_date']) {
              const actual = new Date(grn['actual_delivery_date'] as string)
              const expected = new Date(grn['expected_delivery_date'] as string)
              if (actual <= expected) monthlyBuckets[monthKey].onTime++
            }
          })

          const sortedMonths = Object.entries(monthlyBuckets)
            .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
            .slice(-6)

          const history: HistoricalRating[] = sortedMonths.map(([month, bucket]) => {
            const del = bucket.total > 0 ? (bucket.onTime / bucket.total) * 100 : 0
            const qual = bucket.total > 0 ? (bucket.qualityPassed / bucket.total) * 100 : 0
            return {
              month,
              delivery: Math.round(del * 10) / 10,
              quality: Math.round(qual * 10) / 10,
              overall: Math.round(((del + qual) / 2) * 10) / 10,
            }
          })
          setHistoricalData(history)
        } else {
          setHistoricalData([])
        }

        // Radar chart data - only show metrics with real data
        const radar: Array<{ subject: string; A: number; fullMark: number }> = []
        if (hasGRNData) {
          radar.push({ subject: 'Delivery', A: deliveryScore, fullMark: 100 })
          radar.push({ subject: 'Quality', A: qualityScore, fullMark: 100 })
        }
        setRadarData(radar)

      } catch (error) {
        console.error('Error fetching performance data:', error)
        setPerformanceData([])
        setHistoricalData([])
        setRadarData([])
      } finally {
        setLoading(false)
      }
    }

    fetchPerformanceData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.company, data.targets?.otd, data.targets?.otif, data.targets?.ppm])

  const displayMetrics = performanceData
  const displayHistorical = historicalData
  const displayRadar = radarData

  const overallRating = displayMetrics.find(m => m.name === 'Overall Rating')
  const overallStatus = getRatingStatus(overallRating?.score || 0)

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="space-y-4 animate-pulse">
      {/* Header skeleton */}
      <div className="bg-card rounded-lg shadow-sm p-4">
        <div className="h-6 w-48 bg-gray-200 rounded mb-2" />
        <div className="h-4 w-96 bg-gray-200 rounded" />
      </div>
      {/* Overall rating skeleton */}
      <div className="bg-gray-100 rounded-lg p-6 border-2 border-gray-200">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-gray-200" />
          <div>
            <div className="h-8 w-40 bg-gray-200 rounded mb-2" />
            <div className="h-5 w-24 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
      {/* Metric cards skeleton */}
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-card rounded-lg shadow-sm p-4">
            <div className="h-4 w-24 bg-gray-200 rounded mb-3" />
            <div className="h-8 w-20 bg-gray-200 rounded mb-3" />
            <div className="h-2 w-full bg-gray-200 rounded" />
          </div>
        ))}
      </div>
      {/* Charts skeleton */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-card rounded-lg shadow-sm p-4 h-[350px]">
          <div className="h-5 w-32 bg-gray-200 rounded mb-4" />
          <div className="h-[280px] bg-gray-200 rounded" />
        </div>
        <div className="bg-card rounded-lg shadow-sm p-4 h-[350px]">
          <div className="h-5 w-32 bg-gray-200 rounded mb-4" />
          <div className="h-[280px] bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  )

  // Show loading skeleton while data is being fetched
  if (loading) {
    return <LoadingSkeleton />
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold">Your Performance Ratings</h2>
        <p className="text-sm text-muted-foreground">
          View how your company is rated by customers across key performance indicators
        </p>
      </div>

      {/* Overall Rating Card */}
      <Card className={`shadow-lg ${overallStatus.bg} border-2`} style={{ borderColor: overallStatus.color }}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                style={{ backgroundColor: overallStatus.color }}
              >
                {(overallRating?.score || 0).toFixed(0)}
              </div>
              <div>
                <h3 className="text-2xl font-bold">Overall Rating</h3>
                <p className="text-lg" style={{ color: overallStatus.color }}>{overallStatus.label}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Target: {overallRating?.target}%</span>
                  {overallRating?.trend === 'up' && <TrendingUp className="h-4 w-4 text-green-500" />}
                  {overallRating?.trend === 'down' && <TrendingDown className="h-4 w-4 text-red-500" />}
                </div>
              </div>
            </div>
            <Award className="h-16 w-16" style={{ color: overallStatus.color }} />
          </div>
        </CardContent>
      </Card>

      {/* Metric Cards */}
      <div className="grid grid-cols-4 gap-4">
        {displayMetrics.filter(m => m.name !== 'Overall Rating').map((metric) => {
          const status = getRatingStatus(metric.score)
          return (
            <Card key={metric.name} className="shadow-sm">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">{metric.name}</span>
                  {metric.available && metric.trend === 'up' && <TrendingUp className="h-4 w-4 text-green-500" />}
                  {metric.available && metric.trend === 'down' && <TrendingDown className="h-4 w-4 text-red-500" />}
                </div>
                {metric.available ? (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold" style={{ color: status.color }}>
                        {metric.score.toFixed(1)}%
                      </span>
                      <span className="text-xs text-muted-foreground">/ {metric.target}%</span>
                    </div>
                    <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${metric.score}%`, backgroundColor: status.color }}
                      />
                    </div>
                    <p className="text-xs mt-1" style={{ color: status.color }}>{status.label}</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-muted-foreground">N/A</span>
                    </div>
                    <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden" />
                    <p className="text-xs mt-1 text-muted-foreground">Data not yet available</p>
                  </>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Performance Radar */}
        <ChartWrapper
          title="Performance Profile"
          data={displayRadar}
          exportFilename="performance-radar"
        >
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={displayRadar}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar name="Your Score" dataKey="A" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.5} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </ChartWrapper>

        {/* Historical Trend */}
        <ChartWrapper
          title="Performance Trend"
          data={displayHistorical}
          exportFilename="performance-trend"
          legend={[
            { color: '#10b981', label: 'Quality' },
            { color: '#3b82f6', label: 'Delivery' },
            { color: '#f59e0b', label: 'Overall' },
          ]}
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={displayHistorical}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
              <Legend />
              <Line type="monotone" dataKey="quality" stroke="#10b981" strokeWidth={2} name="Quality" />
              <Line type="monotone" dataKey="delivery" stroke="#3b82f6" strokeWidth={2} name="Delivery" />
              <Line type="monotone" dataKey="overall" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" name="Overall" />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Score Comparison Bar */}
      <ChartWrapper
        title="Score vs Target Comparison"
        data={displayMetrics.filter(m => m.name !== 'Overall Rating')}
        exportFilename="score-vs-target"
        legend={[
          { color: '#3b82f6', label: 'Your Score' },
          { color: '#e5e7eb', label: 'Target' },
        ]}
      >
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={displayMetrics.filter(m => m.name !== 'Overall Rating')} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
            <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
            <Bar dataKey="target" fill="#e5e7eb" name="Target" />
            <Bar dataKey="score" fill="#3b82f6" name="Your Score" />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      {/* Tips for Improvement */}
      <Card className="shadow-sm bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Star className="h-4 w-4 text-amber-500" />
            Tips to Improve Your Rating
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Respond to PO acknowledgment requests within 24 hours to improve responsiveness score</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Submit ASNs before shipment to help customers prepare for receiving</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Keep documentation up to date (certifications, compliance documents)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Proactively communicate any delivery date changes</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Performance ratings are calculated based on your delivery history, quality metrics,
          and responsiveness to customer requests. Ratings are updated daily.
        </p>
      </div>
    </div>
  )
}
